import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

export interface SignUpFormTheme {
  // Branding
  logo?: string;
  appName: string;
  appNameColor?: string;
  
  // Text content
  title?: string;
  description?: string;
  alreadyHaveAccountText?: string;
  loginLinkText?: string;
  createAccountButtonText?: string;
  creatingAccountButtonText?: string;
  termsText?: string;
  
  // Paths
  loginPath?: string;
  termsPath?: string;
  privacyPath?: string;
  successRedirectPath?: string;
  
  // Additional customization
  cardClassName?: string;
  buttonClassName?: string;
  inputClassName?: string;
  logoClassName?: string;
}

export interface SignUpFormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'select' | 'number';
  placeholder?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  helpText?: string;
}

export interface SignUpFormProps {
  theme: SignUpFormTheme;
  fields: SignUpFormField[];
  onSignUp: (data: Record<string, any>) => Promise<void>;
  onError?: (message: string) => void;
  header?: React.ReactNode;
  additionalContent?: React.ReactNode;
  validateForm?: (data: Record<string, any>) => { isValid: boolean; error?: string };
}

export function SignUpForm({
  theme,
  fields,
  onSignUp,
  onError,
  header,
  additionalContent,
  validateForm,
}: SignUpFormProps) {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Custom validation if provided
    if (validateForm) {
      const validation = validateForm(formData);
      if (!validation.isValid) {
        const errorMessage = validation.error || 'Please check your input and try again.';
        setError(errorMessage);
        if (onError) onError(errorMessage);
        return;
      }
    }

    setLoading(true);
    try {
      await onSignUp(formData);
      navigate(theme.successRedirectPath || '/verify-email-pending');
    } catch (error: any) {
      console.error(error);
      
      let errorMessage = '';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email address is already registered. Please log in instead or use a different email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'The email address is invalid. Please check and try again.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'The password is too weak. Please use a stronger password.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email/password sign up is currently disabled. Please contact support.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = error.message || 'An error occurred during sign up. Please try again.';
      }
      
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field: SignUpFormField) => {
    const commonInputClasses = theme.inputClassName || "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

    if (field.type === 'select') {
      return (
        <select
          id={field.name}
          value={formData[field.name] || ''}
          onChange={(e) => handleChange(field.name, e.target.value)}
          className={commonInputClasses}
          required={field.required}
        >
          <option value="">Select {field.label.toLowerCase()}</option>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        id={field.name}
        type={field.type}
        placeholder={field.placeholder}
        value={formData[field.name] || ''}
        onChange={(e) => handleChange(field.name, e.target.value)}
        required={field.required}
        className={commonInputClasses}
      />
    );
  };

  return (
    <div className="flex min-h-screen flex-col">
      {header}
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className={theme.cardClassName || "w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-sm"}>
          <div className="flex flex-col space-y-1.5 p-6 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center space-x-2">
              {theme.logo && (
                <img 
                  src={theme.logo} 
                  alt={`${theme.appName} Logo`} 
                  className={theme.logoClassName || "h-10 w-10"}
                />
              )}
              <span className={`text-xl font-bold ${theme.appNameColor || 'text-navy'}`}>
                {theme.appName}
              </span>
            </div>
            <h3 className="text-2xl font-semibold leading-none tracking-tight">
              {theme.title || 'Create Your Account'}
            </h3>
            {theme.description && (
              <p className="text-sm text-muted-foreground">
                {theme.description}
              </p>
            )}
          </div>
          <div className="p-6 pt-0">
            {error && (
              <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {fields.map((field) => (
                <div key={field.name} className="space-y-2">
                  <label 
                    htmlFor={field.name} 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {field.label}
                  </label>
                  {renderField(field)}
                  {field.helpText && (
                    <p className="text-xs text-muted-foreground">
                      {field.helpText}
                    </p>
                  )}
                </div>
              ))}

              <button 
                type="submit" 
                className={theme.buttonClassName || "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 w-full"}
                disabled={loading}
              >
                {loading ? (theme.creatingAccountButtonText || 'Creating Account...') : (theme.createAccountButtonText || 'Create Account')}
              </button>
            </form>

            {theme.loginPath && (
              <div className="mt-4 text-center text-sm">
                <span className="text-muted-foreground">
                  {theme.alreadyHaveAccountText || 'Already have an account? '}
                </span>
                <Link to={theme.loginPath} className="text-primary hover:underline">
                  {theme.loginLinkText || 'Log in'}
                </Link>
              </div>
            )}

            {(theme.termsPath || theme.privacyPath) && (
              <div className="mt-4 text-xs text-muted-foreground">
                {theme.termsText || 'By creating an account, you agree to our'}{' '}
                {theme.termsPath && (
                  <>
                    <Link to={theme.termsPath} className="underline">
                      Terms of Service
                    </Link>
                    {theme.privacyPath && ' and '}
                  </>
                )}
                {theme.privacyPath && (
                  <Link to={theme.privacyPath} className="underline">
                    Privacy Policy
                  </Link>
                )}
                .
              </div>
            )}

            {additionalContent}
          </div>
        </div>
      </div>
    </div>
  );
}

